/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2024.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QExposedJoin} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QExposedJoin";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem, {ListItemProps} from "@mui/material/ListItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import {getQueryScreenColumnGroup} from "qqq/utils/qqq/QueryScreenFieldHints";
import React, {useEffect, useMemo, useState} from "react";

export interface FieldListMenuContentProps
{
   idPrefix: string;
   heading?: string;
   placeholder?: string;
   tableMetaData: QTableMetaData;
   showTableHeaderEvenIfNoExposedJoins?: boolean;
   fieldNamesToHide?: string[];
   isModeSelectOne?: boolean;
   handleSelectedField?: (field: QFieldMetaData, table: QTableMetaData) => void;
   isModeToggle?: boolean;
   toggleStates?: { [fieldName: string]: boolean };
   handleToggleField?: (field: QFieldMetaData, table: QTableMetaData, newValue: boolean) => void;
   fieldEndAdornment?: JSX.Element;
   handleAdornmentClick?: (field: QFieldMetaData, table: QTableMetaData, event: React.MouseEvent<any>) => void;
   omitExposedJoins?: string[];
   requiredCapability?: Capability;
   /** true while the containing surface (Menu/Dialog) is open; drives focus + initial highlight */
   isOpen: boolean;
   /** called to close the containing surface (was closeMenu in FieldListMenu) */
   onClose: () => void;
}

/*******************************************************************************
 ** one section of the list: a table's ordinary fields, or (when group is set) the
 ** main table's fields that share a queryScreenColumnGroup hint.
 *******************************************************************************/
interface TableWithFields
{
   table?: QTableMetaData;
   fields: QFieldMetaData[];
   group?: { key: string; label: string };
}


/*******************************************************************************
 ** key under which a section keeps its collapsed / toggle state
 *******************************************************************************/
function sectionKey(section: TableWithFields, mainTable: QTableMetaData): string
{
   return (section.group?.key ?? section.table?.name ?? mainTable.name);
}

/*******************************************************************************
 ** The body of FieldListMenu - a list of fields from a table (and its join
 ** tables) which can be interacted with (select-one, toggle, or via an
 ** end-adornment).  Extracted from FieldListMenu so it can be rendered both
 ** inside the anchored <Menu> (Query Screen) and inside a <Dialog> (Record
 ** Screen field-jump modal).
 *******************************************************************************/
export default function FieldListMenuContent({idPrefix, heading, placeholder, tableMetaData, showTableHeaderEvenIfNoExposedJoins, fieldNamesToHide, isModeSelectOne, handleSelectedField, isModeToggle, toggleStates, handleToggleField, fieldEndAdornment, handleAdornmentClick, omitExposedJoins, requiredCapability, isOpen, onClose}: FieldListMenuContentProps): JSX.Element
{
   const [searchText, setSearchText] = useState("");
   const [focusedIndex, setFocusedIndex] = useState(null as number);

   const [fieldsByTable, setFieldsByTable] = useState([] as TableWithFields[]);
   const [collapsedTables, setCollapsedTables] = useState({} as { [tableName: string]: boolean });

   const [lastMouseOverXY, setLastMouseOverXY] = useState({x: 0, y: 0});
   const [timeOfLastArrow, setTimeOfLastArrow] = useState(0);

   const availableExposedJoins = useMemo(() =>
   {
      const rs: QExposedJoin[] = []
      for(let exposedJoin of tableMetaData.exposedJoins ?? [])
      {
         if(omitExposedJoins?.indexOf(exposedJoin.joinTable.name) > -1)
         {
            continue;
         }
         rs.push(exposedJoin);
      }
      return (rs);
   }, [tableMetaData, omitExposedJoins]);

   //////////////////
   // check usages //
   //////////////////
   if (isModeSelectOne)
   {
      if (!handleSelectedField)
      {
         throw ("In FieldListMenuContent, if isModeSelectOne=true, then a callback for handleSelectedField must be provided.");
      }
   }

   if (isModeToggle)
   {
      if (!toggleStates)
      {
         throw ("In FieldListMenuContent, if isModeToggle=true, then a model for toggleStates must be provided.");
      }
      if (!handleToggleField)
      {
         throw ("In FieldListMenuContent, if isModeToggle=true, then a callback for handleToggleField must be provided.");
      }
   }

   /////////////////////
   // init some stuff //
   /////////////////////
   if (fieldsByTable.length == 0)
   {
      collapsedTables[tableMetaData.name] = false;

      ///////////////////////////////////////////////////////////////////////////////////////////////
      // the main table's ordinary fields come first (with its meta data only if there are exposed //
      // joins, as before); fields that name a column group follow as their own collapsed          //
      // sections; then the join tables                                                            //
      ///////////////////////////////////////////////////////////////////////////////////////////////
      const ordinaryFields: QFieldMetaData[] = [];
      const groupedFields: { [label: string]: QFieldMetaData[] } = {};
      for (const field of getTableFieldsAsAlphabeticalArray(tableMetaData))
      {
         const group = getQueryScreenColumnGroup(field);
         if (group)
         {
            (groupedFields[group] = groupedFields[group] ?? []).push(field);
         }
         else
         {
            ordinaryFields.push(field);
         }
      }

      fieldsByTable.push(availableExposedJoins?.length > 0 ? {table: tableMetaData, fields: ordinaryFields} : {fields: ordinaryFields});

      for (const label of Object.keys(groupedFields).sort())
      {
         const key = `${tableMetaData.name}#${label}`;
         fieldsByTable.push({table: tableMetaData, group: {key: key, label: label}, fields: groupedFields[label]});

         //////////////////////////////////////////////////////////////////////////////////////
         // start named groups collapsed where the user can expand them (toggle mode has the //
         // expander); in select-one mode there is no expander, so leave them open           //
         //////////////////////////////////////////////////////////////////////////////////////
         collapsedTables[key] = !!isModeToggle;
      }

      for (let i = 0; i < availableExposedJoins?.length; i++)
      {
         const joinTable = availableExposedJoins[i].joinTable;

         //////////////////////////////////////////////////////////////////////////////////////////
         // a join table's grouped fields get their own sections too, so the join table's toggle //
         // leaves them alone - e.g. Order's summary column, seen from the Line Item screen      //
         //////////////////////////////////////////////////////////////////////////////////////////
         const joinOrdinaryFields: QFieldMetaData[] = [];
         const joinGroupedFields: { [label: string]: QFieldMetaData[] } = {};
         for (const field of getTableFieldsAsAlphabeticalArray(joinTable))
         {
            const group = getQueryScreenColumnGroup(field);
            if (group)
            {
               (joinGroupedFields[group] = joinGroupedFields[group] ?? []).push(field);
            }
            else
            {
               joinOrdinaryFields.push(field);
            }
         }

         fieldsByTable.push({table: joinTable, fields: joinOrdinaryFields});
         collapsedTables[joinTable.name] = false;

         for (const label of Object.keys(joinGroupedFields).sort())
         {
            const key = `${joinTable.name}#${label}`;
            fieldsByTable.push({table: joinTable, group: {key: key, label: `${joinTable.label} ${label}`}, fields: joinGroupedFields[label]});
            collapsedTables[key] = !!isModeToggle;
         }
      }

      setFieldsByTable(fieldsByTable);
      setCollapsedTables(collapsedTables);
   }


   /*******************************************************************************
    ** on open of the containing surface, focus the search box and highlight the
    ** first row (was handled by FieldListMenu.openMenu before the extraction).
    *******************************************************************************/
   useEffect(() =>
   {
      if (isOpen)
      {
         //////////////////////////////////////////////////////////////////////////
         // reset synchronously first: the Query Menu is keepMounted, so a stale //
         // focusedIndex survives across closes.  otherwise the previously-      //
         // highlighted row flashes for a frame on reopen before the tick below  //
         // snaps it back to row 0.                                              //
         //////////////////////////////////////////////////////////////////////////
         setFocusedIndex(null);
         setTimeout(() =>
         {
            document.getElementById(`field-list-dropdown-${idPrefix}-textField`)?.focus();
            doSetFocusedIndex(0, true);
         });
      }
      ///////////////////////////////////////////////////////////////////////////////
      // intentionally only depends on isOpen: this is a run-on-open effect (focus //
      // the search box + highlight the first row when the surface opens).         //
      // idPrefix/doSetFocusedIndex are stable for the life of the surface.        //
      ///////////////////////////////////////////////////////////////////////////////
   }, [isOpen]);


   /*******************************************************************************
    **
    *******************************************************************************/
   function getTableFieldsAsAlphabeticalArray(table: QTableMetaData): QFieldMetaData[]
   {
      const fields: QFieldMetaData[] = [];
      const seenFields: Record<string, true> = {};

      const fieldPusher = (field: QFieldMetaData) =>
      {
         let fullFieldName = field.name;
         if (table.name != tableMetaData.name)
         {
            fullFieldName = `${table.name}.${field.name}`;
         }

         if(seenFields[fullFieldName])
         {
            return;
         }

         if (requiredCapability !== undefined && !field.capabilities.has(requiredCapability))
         {
            return;
         }

         if (fieldNamesToHide && fieldNamesToHide.indexOf(fullFieldName) > -1)
         {
            return;
         }

         seenFields[fullFieldName] = true;
         fields.push(field);
      }

      table.fields.forEach(field => fieldPusher(field));
      table.virtualFields?.forEach(field => fieldPusher(field));

      fields.sort((a, b) => a.label.localeCompare(b.label));
      return (fields);
   }

   const fieldsByTableToShow: TableWithFields[] = [];
   let maxFieldIndex = 0;
   fieldsByTable.forEach((tableWithFields) =>
   {
      let fieldsToShowForThisTable = tableWithFields.fields.filter(doesFieldMatchSearchText);
      if (fieldsToShowForThisTable.length > 0)
      {
         fieldsByTableToShow.push({table: tableWithFields.table, group: tableWithFields.group, fields: fieldsToShowForThisTable});
         maxFieldIndex += fieldsToShowForThisTable.length;
      }
   });


   /*******************************************************************************
    **
    *******************************************************************************/
   function getShownFieldAndTableByIndex(targetIndex: number): { field: QFieldMetaData, table: QTableMetaData }
   {
      let index = -1;
      for (let i = 0; i < fieldsByTableToShow.length; i++)
      {
         const tableWithField = fieldsByTableToShow[i];
         for (let j = 0; j < tableWithField.fields.length; j++)
         {
            index++;

            if (index == targetIndex)
            {
               return {field: tableWithField.fields[j], table: tableWithField.table};
            }
         }
      }

      return (null);
   }


   /*******************************************************************************
    ** event handler for keys presses
    *******************************************************************************/
   function keyDown(event: any)
   {
      /////////////////////////////////////////////
      // console.log(`Event key: ${event.key}`); //
      /////////////////////////////////////////////
      setTimeout(() => document.getElementById(`field-list-dropdown-${idPrefix}-textField`)?.focus());

      if (isModeSelectOne && event.key == "Enter" && focusedIndex != null)
      {
         setTimeout(() =>
         {
            event.stopPropagation();

            //////////////////////////////////////////////////////////////////////////
            // focusedIndex can be stale/out of range (e.g. the filter narrowed to  //
            // no matches after a mistype), in which case there's no selection -    //
            // getShownFieldAndTableByIndex returns null.  Only close + select when //
            // there's a real match; otherwise no-op and leave the menu open.       //
            //////////////////////////////////////////////////////////////////////////
            const selected = getShownFieldAndTableByIndex(focusedIndex);
            if (selected?.field)
            {
               onClose();
               handleSelectedField(selected.field, selected.table ?? tableMetaData);
            }
         });
         return;
      }

      const keyOffsetMap: { [key: string]: number } = {
         "End": 10000,
         "Home": -10000,
         "ArrowDown": 1,
         "ArrowUp": -1,
         "PageDown": 5,
         "PageUp": -5,
      };

      const offset = keyOffsetMap[event.key];
      if (offset)
      {
         event.stopPropagation();
         setTimeOfLastArrow(new Date().getTime());

         if (isModeSelectOne)
         {
            let startIndex = focusedIndex;
            if (offset > 0)
            {
               /////////////////
               // a down move //
               /////////////////
               if (startIndex == null)
               {
                  startIndex = -1;
               }

               let goalIndex = startIndex + offset;
               if (goalIndex > maxFieldIndex - 1)
               {
                  goalIndex = maxFieldIndex - 1;
               }

               doSetFocusedIndex(goalIndex, true);
            }
            else
            {
               ////////////////
               // an up move //
               ////////////////
               let goalIndex = startIndex + offset;
               if (goalIndex < 0)
               {
                  goalIndex = 0;
               }

               doSetFocusedIndex(goalIndex, true);
            }
         }
      }
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function doSetFocusedIndex(i: number, tryToScrollIntoView: boolean): void
   {
      if (isModeSelectOne)
      {
         setFocusedIndex(i);

         if (tryToScrollIntoView)
         {
            const element = document.getElementById(`field-list-dropdown-${idPrefix}-${i}`);
            element?.scrollIntoView({block: "center"});
         }
      }
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function setFocusedField(field: QFieldMetaData, table: QTableMetaData, tryToScrollIntoView: boolean)
   {
      let index = -1;
      for (let i = 0; i < fieldsByTableToShow.length; i++)
      {
         const tableWithField = fieldsByTableToShow[i];
         for (let j = 0; j < tableWithField.fields.length; j++)
         {
            const loopField = tableWithField.fields[j];
            index++;

            ////////////////////////////////////////////////////////////////////////////////////
            // the main table's own section carries no table when there are no exposed joins, //
            // but its named groups always do - so treat a missing table as the main table    //
            ////////////////////////////////////////////////////////////////////////////////////
            const tableMatches = (table == null || table.name == (tableWithField.table ?? tableMetaData).name);
            if (tableMatches && field.name == loopField.name)
            {
               doSetFocusedIndex(index, tryToScrollIntoView);
               return;
            }
         }
      }
   }


   /*******************************************************************************
    ** event handler for mouse-over the menu
    *******************************************************************************/
   function handleMouseOver(event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLLIElement>, field: QFieldMetaData, table: QTableMetaData)
   {
      //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // so we're trying to fix the case where, if you put your mouse over an element, but then press up/down arrows,                                     //
      // where the mouse will become over a different element after the scroll, and the focus will follow the mouse instead of keyboard.                  //
      // the last x/y isn't really useful, because the mouse generally isn't left exactly where it was when the mouse-over happened (edge of the element) //
      // but the keyboard last-arrow time that we capture, that's what's actually being useful in here                                                    //
      //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      if (event.clientX == lastMouseOverXY.x && event.clientY == lastMouseOverXY.y)
      {
         //////////////////////////////////////////////////////////
         // console.log("mouse didn't move, so, doesn't count"); //
         //////////////////////////////////////////////////////////
         return;
      }

      const now = new Date().getTime();
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      // console.log(`Compare now [${now}] to last arrow [${timeOfLastArrow}] (diff: [${now - timeOfLastArrow}])`); //
      ////////////////////////////////////////////////////////////////////////////////////////////////////////////////
      if (now < timeOfLastArrow + 300)
      {
         ////////////////////////////////////////////////////////////////////////////////////////
         // console.log("An arrow event happened less than 300 mills ago, so doesn't count."); //
         ////////////////////////////////////////////////////////////////////////////////////////
         return;
      }

      ////////////////////////////////////////
      // console.log("yay, mouse over..."); //
      ////////////////////////////////////////
      setFocusedField(field, table, false);
      setLastMouseOverXY({x: event.clientX, y: event.clientY});
   }


   /*******************************************************************************
    ** event handler for text input changes
    *******************************************************************************/
   function updateSearch(event: React.ChangeEvent<HTMLInputElement>)
   {
      setSearchText(event?.target?.value ?? "");
      doSetFocusedIndex(0, true);
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function doesFieldMatchSearchText(field: QFieldMetaData): boolean
   {
      if (searchText == "")
      {
         return (true);
      }

      const columnLabelMinusTable = field.label.replace(/.*: /, "");
      if (columnLabelMinusTable.toLowerCase().startsWith(searchText.toLowerCase()))
      {
         return (true);
      }

      try
      {
         ////////////////////////////////////////////////////////////
         // try to match word-boundary followed by the filter text //
         // e.g., "name" would match "First Name" or "Last Name"   //
         ////////////////////////////////////////////////////////////
         const re = new RegExp("\\b" + searchText.toLowerCase());
         if (columnLabelMinusTable.toLowerCase().match(re))
         {
            return (true);
         }
      }
      catch (e)
      {
         //////////////////////////////////////////////////////////////////////////////////
         // in case text is an invalid regex... well, at least do a starts-with match... //
         //////////////////////////////////////////////////////////////////////////////////
         if (columnLabelMinusTable.toLowerCase().startsWith(searchText.toLowerCase()))
         {
            return (true);
         }
      }

      const tableLabel = field.label.replace(/:.*/, "");
      if (tableLabel)
      {
         try
         {
            ////////////////////////////////////////////////////////////
            // try to match word-boundary followed by the filter text //
            // e.g., "name" would match "First Name" or "Last Name"   //
            ////////////////////////////////////////////////////////////
            const re = new RegExp("\\b" + searchText.toLowerCase());
            if (tableLabel.toLowerCase().match(re))
            {
               return (true);
            }
         }
         catch (e)
         {
            //////////////////////////////////////////////////////////////////////////////////
            // in case text is an invalid regex... well, at least do a starts-with match... //
            //////////////////////////////////////////////////////////////////////////////////
            if (tableLabel.toLowerCase().startsWith(searchText.toLowerCase()))
            {
               return (true);
            }
         }
      }

      return (false);
   }


   /*******************************************************************************
    ** Event handler for toggling a field in toggle mode
    *******************************************************************************/
   function handleFieldToggle(event: React.ChangeEvent<HTMLInputElement>, field: QFieldMetaData, table: QTableMetaData)
   {
      event.stopPropagation();
      handleToggleField(field, table, event.target.checked);
   }


   /*******************************************************************************
    ** Event handler for toggling a whole section (a table, or a named group) in
    ** toggle mode - only the fields listed in that section, so a table's switch
    ** never touches fields that live in a named group.
    *******************************************************************************/
   function handleSectionToggle(event: React.ChangeEvent<HTMLInputElement>, section: TableWithFields)
   {
      event.stopPropagation();
      const table = section.table ?? tableMetaData;
      for (const field of section.fields)
      {
         if (doesFieldMatchSearchText(field))
         {
            handleToggleField(field, table, event.target.checked);
         }
      }
   }


   ///////////////////////////////////////////////////////////
   // compute the section-level toggle state & count values //
   ///////////////////////////////////////////////////////////
   const tableToggleStates: { [sectionStateKey: string]: boolean } = {};
   const tableToggleCounts: { [sectionStateKey: string]: number } = {};

   if (isModeToggle)
   {
      for (const section of fieldsByTable)
      {
         const {allOn, count} = getSectionToggleState(section);
         tableToggleStates[sectionKey(section, tableMetaData)] = allOn;
         tableToggleCounts[sectionKey(section, tableMetaData)] = count;
      }
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function getSectionToggleState(section: TableWithFields): { allOn: boolean, count: number }
   {
      const isMainTable = !section.table || section.table.name == tableMetaData.name;

      let allOn = true;
      let count = 0;
      for (const field of section.fields)
      {
         const name = isMainTable ? field.name : `${section.table.name}.${field.name}`;
         if (!toggleStates[name])
         {
            allOn = false;
         }
         else
         {
            count++;
         }
      }

      return ({allOn: allOn, count: count});
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function toggleCollapsedTable(tableName: string)
   {
      collapsedTables[tableName] = !collapsedTables[tableName];
      setCollapsedTables(Object.assign({}, collapsedTables));
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   function doHandleAdornmentClick(field: QFieldMetaData, table: QTableMetaData, event: React.MouseEvent<any>)
   {
      ////////////////////////////////////////////////////////////////////////////////////////
      // stop the click bubbling: in toggle mode it would flip the row's Switch; in         //
      // select-one mode it would fire the row-level onClick (handleSelectedField) a second //
      // time.  either way the adornment must be the sole action for this click.            //
      ////////////////////////////////////////////////////////////////////////////////////////
      if (isModeToggle || isModeSelectOne)
      {
         event.preventDefault();
         event.stopPropagation();
      }
      onClose();
      if (handleAdornmentClick)
      {
         handleAdornmentClick(field, table, event);
      }
   }


   let index = -1;
   const textFieldId = `field-list-dropdown-${idPrefix}-textField`;
   let listItemPadding = isModeToggle ? "0.125rem" : "0.5rem";

   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////
   // for z-indexes, we set each table header to i+1, then the fields in that table to i (so they go behind it) //
   // then we increment i by 2 for the next table (so the next header goes above the previous header)           //
   // this fixes a thing where, if one table's name wrapped to 2 lines, then when the next table below it would //
   // come up, if it was only 1 line, then the second line from the previous one would bleed through.           //
   ///////////////////////////////////////////////////////////////////////////////////////////////////////////////
   let zIndex = 1;

   return (
      <Box width={isModeToggle ? "305px" : "265px"} borderRadius={2} className={`fieldListMenuBody fieldListMenuBody-${idPrefix}`} onKeyDown={keyDown}>
         {
            heading &&
            <Box px={1} py={0.5} fontWeight={"700"}>
               {heading}
            </Box>
         }
         <Box p={1} pt={0.5}>
            <TextField id={textFieldId} variant="outlined" placeholder={placeholder ?? "Search Fields"} fullWidth value={searchText} onChange={updateSearch} onKeyDown={keyDown} inputProps={{sx: {pr: "2rem"}, autoComplete: "off"}} />
            {
               searchText != "" && <IconButton sx={{position: "absolute", right: "0.5rem", top: "0.5rem"}} onClick={() =>
               {
                  updateSearch(null);
                  document.getElementById(textFieldId).focus();
               }}><Icon fontSize="small">close</Icon></IconButton>
            }
         </Box>
         <Box maxHeight={"445px"} overflow="auto" mr={"-0.5rem"} sx={{scrollbarGutter: "stable"}}>
            <List sx={{pl: "0.5rem", pr: isModeToggle && fieldEndAdornment ? 0 : "0.5rem", cursor: "default"}}>
               {
                  fieldsByTableToShow.map((tableWithFields) =>
                  {
                     let headerContents = null;
                     const headerTable = tableWithFields.table || tableMetaData;
                     const sectionStateKey = sectionKey(tableWithFields, tableMetaData);
                     const sectionLabel = tableWithFields.group ? tableWithFields.group.label : `${headerTable.label} Fields`;
                     if (tableWithFields.table || showTableHeaderEvenIfNoExposedJoins || tableWithFields.group)
                     {
                        headerContents = (<b>{sectionLabel}</b>);
                     }

                     if (isModeToggle)
                     {
                        headerContents = (<FormControlLabel
                           sx={{display: "flex", alignItems: "flex-start", "& .MuiFormControlLabel-label": {lineHeight: "1.4", fontWeight: "500 !important"}}}
                           control={<Switch
                              size="small"
                              sx={{top: "1px"}}
                              checked={tableToggleStates[sectionStateKey]}
                              onChange={(event) => handleSectionToggle(event, tableWithFields)}
                           />}
                           label={<span style={{marginTop: "0.25rem", display: "inline-block"}}><b>{sectionLabel}</b>&nbsp;<span style={{fontWeight: 400}}>({tableToggleCounts[sectionStateKey]})</span></span>} />);
                     }

                     if (isModeToggle)
                     {
                        headerContents = (
                           <>
                              <IconButton
                                 onClick={() => toggleCollapsedTable(sectionStateKey)}
                                 aria-label={`${collapsedTables[sectionStateKey] ? "expand" : "collapse"} ${sectionLabel}`}
                                 sx={{justifyContent: "flex-start", fontSize: "0.875rem", pt: 0.5, pb: 0, mr: "0.25rem"}}
                                 disableRipple={true}
                              >
                                 <Icon sx={{fontSize: "1.5rem !important", position: "relative", top: "2px"}}>{collapsedTables[sectionStateKey] ? "expand_less" : "expand_more"}</Icon>
                              </IconButton>
                              {headerContents}
                           </>
                        );
                     }

                     let marginLeft = "unset";
                     if (isModeToggle)
                     {
                        marginLeft = "-1rem";
                     }

                     zIndex += 2;

                     return (
                        <React.Fragment key={sectionStateKey}>
                           <>
                              {headerContents && <ListItem sx={{position: "sticky", top: -1, zIndex: zIndex + 1, padding: listItemPadding, ml: marginLeft, display: "flex", alignItems: "flex-start", ...(isModeToggle ? {width: "calc(100% + 1rem)"} : {}), backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,1), rgba(255,255,255,1) 90%, rgba(255,255,255,0))"}}>{headerContents}</ListItem>}
                              {
                                 tableWithFields.fields.map((field) =>
                                 {
                                    index++;
                                    const key = `${tableWithFields.table?.name}-${field.name}`;

                                    if (collapsedTables[sectionStateKey])
                                    {
                                       return (<React.Fragment key={key} />);
                                    }

                                    let style = {};
                                    if (index == focusedIndex)
                                    {
                                       style = {backgroundColor: "#EFEFEF"};
                                    }

                                    const onClick: ListItemProps = {};
                                    if (isModeSelectOne)
                                    {
                                       onClick.onClick = () =>
                                       {
                                          onClose();
                                          handleSelectedField(field, tableWithFields.table ?? tableMetaData);
                                       };
                                    }

                                    let label: JSX.Element | string = field.label;
                                    const fullFieldName = tableWithFields.table && tableWithFields.table.name != tableMetaData.name ? `${tableWithFields.table.name}.${field.name}` : field.name;

                                    if (fieldEndAdornment)
                                    {
                                       label = <Box flex="1 1 auto" minWidth={0} display="flex" justifyContent="space-between" alignItems="center">
                                          {label}
                                          <Box onClick={(event) => doHandleAdornmentClick(field, tableWithFields.table, event)}>
                                             {fieldEndAdornment}
                                          </Box>
                                       </Box>;
                                    }

                                    let contents = <>{label}</>;
                                    let paddingLeft = "0.5rem";

                                    if (isModeToggle)
                                    {
                                       ///////////////////////////////////////////////////////////////////////////////////
                                       // when an end-adornment is present, make the label slot itself a flex container //
                                       // that fills the row, so the wrapping Box (a flex child) grows to fill it and   //
                                       // its space-between can right-align the adornment                               //
                                       ///////////////////////////////////////////////////////////////////////////////////
                                       const labelSx: any = {lineHeight: "1.4", color: "#606060", fontWeight: "500 !important"};
                                       const formControlLabelSx: any = {display: "flex", alignItems: "flex-start"};
                                       if (fieldEndAdornment)
                                       {
                                          labelSx.display = "flex";
                                          labelSx.flexGrow = 1;
                                          labelSx.minWidth = 0;
                                          formControlLabelSx.width = "100%";
                                          formControlLabelSx.mr = 0;
                                       }
                                       formControlLabelSx["& .MuiFormControlLabel-label"] = labelSx;
                                       contents = (<FormControlLabel
                                          sx={formControlLabelSx}
                                          control={<Switch
                                             size="small"
                                             sx={{top: "-3px"}}
                                             checked={toggleStates[fullFieldName]}
                                             onChange={(event) => handleFieldToggle(event, field, tableWithFields.table)}
                                          />}
                                          label={label} />);
                                       paddingLeft = "2.5rem";
                                    }

                                    return <ListItem
                                       key={key}
                                       id={`field-list-dropdown-${idPrefix}-${index}`}
                                       sx={{color: "#757575", p: 1, borderRadius: ".5rem", padding: listItemPadding, pl: paddingLeft, pr: isModeToggle && fieldEndAdornment ? 0 : undefined, scrollMarginTop: "3rem", zIndex: zIndex, background: "#FFFFFF", ...style}}
                                       onMouseOver={(event) => handleMouseOver(event, field, tableWithFields.table)}
                                       {...onClick}
                                    >{contents}</ListItem>;
                                 })
                              }
                           </>
                        </React.Fragment>
                     );
                  })
               }
               {
                  index == -1 && <ListItem sx={{p: "0.5rem"}}><i>No fields found.</i></ListItem>
               }
            </List>
         </Box>
      </Box>
   );
}
